<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->
<div class="event-template-page-container">
@if (!isIFrame) {
<app-topbar
  [title]="titleText"
  [topbarView]="TopbarView.ALLOY_HOME"
  >
</app-topbar>
}
@if (permissions && !isIFrame) {
<div class="top-row">
  <div class="one-cell last-cell">
    @if (canViewAdministration) {
    <button aria-label="Show Administration Page" mat-icon-button [routerLink]="['/admin']"
      style="outline: none;" title="Administration">
      <mat-icon class="mdi-24px center-self" fontIcon="mdi-cog"></mat-icon>
    </button>
    }
  </div>
</div>
}
<div class="cssLayoutRowCenterCenter">
  <div class="event-info-container elevate">
    <div class="cssLayoutRowStartCenter full-width">
      <div class="sp-icon">
        <mat-icon class="alloy-icon" svgIcon="ic_crucible_alloy"></mat-icon>
      </div>
      <div>
        <h2 class="text title-text">Event</h2>
      </div>
      <div class="cssLayoutRowEndCenter full-width">
        <img class="crucible-logo" [src]="
            (theme$ | async) === 'light-theme'
              ? 'assets/img/crucible-logo-light.png'
              : 'assets/img/crucible-logo-dark.png'
          " />
      </div>
    </div>

    @if (isLoading$ | async) {
    <mat-card appearance="outlined" class="spinner-container">
      <mat-progress-spinner color="primary" mode="indeterminate">
      </mat-progress-spinner>
    </mat-card>
    }

    @if (eventTemplate$ | async; as eventTemplate) {
    <div>
      <mat-divider></mat-divider>
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            <h1 class="template-name">
              {{ eventTemplate.name }}
            </h1>
          </mat-card-title>
          <mat-card-subtitle></mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
          <div class="description-text cssLayoutRowStartCenter">
            {{ eventTemplate.description }}
          </div>
        </mat-card-content>
        @if (!currentEvent) {
        <div class="cssLayoutColumnSpaceCenter">
          <div class="cssLayoutRowCenterCenter">
            @if (userEvents.length > 0) {
            <div>
              <button mat-raised-button [matMenuTriggerFor]="join" class="event-button">
                Join
                <mat-icon svgIcon="ic_expand_more_black_24px"></mat-icon>
              </button>
              <mat-menu #join="matMenu" [overlapTrigger]="false">
                @for (element of userEvents; track element) {
                <button mat-menu-item (click)="rejoinEvent(element)">
                  Invited By: {{ element.username }}
                </button>
                }
              </mat-menu>
            </div>
            }
            @if (!isIFrame) {
            <button class="event-button-end" matButton="filled" (click)="launchEvent(eventTemplate.id)">
              Launch
            </button>
            }
          </div>
          <label class="duration-label">Duration: {{ eventTemplate.durationHours }} hours</label>
        </div>
        }
        @if (currentEvent) {
        @switch (determineEventStatus(currentEvent)) {
        <!-- FAILED EVENT -->
        @case (ALLOY_CURRENT_EVENT_STATUS.FAILED) {
        Event Failed
        @if (failureMessage !== '') {
        <div>
          <div class="failure-text">Failure: {{ failureMessage }}</div>
          <div>
            Event ended. Please try again later or contact an
            administrator.
          </div>
        </div>
        }
        }
        <!-- ACTIVE EVENT -->
        @case (ALLOY_CURRENT_EVENT_STATUS.LAUNCHED) {
        <div class="cssLayoutColumnSpaceCenter">
          <div class="cssLayoutRowSpaceCenter">
            @if (userEvents.length > 0) {
            <div>
              <button mat-raised-button [matMenuTriggerFor]="join" class="event-button">
                Join
                <mat-icon svgIcon="ic_expand_more_black_24px"></mat-icon>
              </button>
              <mat-menu #join="matMenu" [overlapTrigger]="false">
                @for (element of userEvents; track element) {
                <button mat-menu-item (click)="rejoinEvent(element)">
                  Invited By: {{ element.username }}
                </button>
                }
              </mat-menu>
            </div>
            }
            @if (!isIFrame) {
            <button class="event-button-end" matButton="filled" (click)="rejoinEvent(currentEvent)">
              Open Event
            </button>
            }
            @if (isOwner) {
            <button class="event-button-end" matButton="filled" (click)="endEvent(currentEvent)">
              End Event
            </button>
            <div style="display: inline-block" matTooltip="Create An Invite Link">
              <button matButton="filled" [matMenuTriggerFor]="invite" class="event-button-end">
                Invite
                <mat-icon svgIcon="ic_expand_more_white_24px"></mat-icon>
              </button>
              <mat-menu #invite="matMenu" [overlapTrigger]="false">
                @if (!currentEvent?.shareCode) {
                <button mat-menu-item (click)="inviteEvent(currentEvent)">
                  Generate Link
                </button>
                }
                @if (currentEvent?.shareCode) {
                <button mat-menu-item ngxClipboard (click)="copyInviteLink(currentEvent)"
                  title="Copy:  {{ inviteLink }}" class="copy-button">
                  <mat-icon class="icon-20px" svgIcon="ic_clipboard_copy"></mat-icon>
                  Copy Link
                </button>
                }
                @if (currentEvent?.shareCode) {
                <mat-slide-toggle class="mat-menu-item" color="primary" [(ngModel)]="inviteShown">
                  Show Link
                </mat-slide-toggle>
                }
              </mat-menu>
            </div>
            @if (false) {
            <button class="event-button-end" mat-raised-button color="warn" (click)="redeployEvent(currentEvent)"
              [disabled]="redeploying">
              @if (redeploying) {
              <div>Redeploying...</div>
              }
              @if (!redeploying) {
              <div>Redeploy Event</div>
              }
            </button>
            }
            }
          </div>
          @if (!timeRunningLow) {
          <label class="duration-label">{{
            remainingTime
            }}</label>
          }
          @if (timeRunningLow) {
          <label class="duration-label-warn">{{
            remainingTime
            }}</label>
          }
          <div [hidden]="!inviteShown">
            {{ inviteLink }}
          </div>
        </div>
        }
        <!-- LAUNCHING -->
        @case (ALLOY_CURRENT_EVENT_STATUS.LAUNCHING) {
        <div class="cssLayoutColumnSpaceCenter">
          <h2>Please wait!</h2>
          <mat-progress-spinner color="primary" mode="indeterminate"></mat-progress-spinner>
          @if (currentEvent !== undefined) {
          <h3>
            Status: {{ currentEvent.status }}
          </h3>
          }
          @if (currentEvent === undefined) {
          <h3>Status: -</h3>
          }
        </div>
        }
        <!-- ENDING -->
        @case (ALLOY_CURRENT_EVENT_STATUS.ENDING) {
        <div class="cssLayoutColumnSpaceCenter">
          <h2>Please wait!</h2>
          <mat-progress-spinner color="primary" mode="indeterminate"></mat-progress-spinner>
          @if (currentEvent !== undefined) {
          <h3>
            Status: {{ currentEvent.status }}
          </h3>
          }
          @if (currentEvent === undefined) {
          <h3>Status: -</h3>
          }
        </div>
        }
        }
        }
      </mat-card>
      <mat-divider [inset]="true"></mat-divider>
      @if (events$ | async; as events) {
      <mat-accordion>
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title> History </mat-panel-title>
          </mat-expansion-panel-header>
          <div>
            <mat-table #historySort="matSort" class="history-table" [dataSource]="impsDataSource" matSort>
              <!--- Note that these columns can be defined in any order.
                  The actual rendered columns are set as a property on the row eventTemplate" -->
              <ng-container matColumnDef="username">
                <mat-header-cell *matHeaderCellDef mat-sort-header> User </mat-header-cell>
                <mat-cell *matCellDef="let element">
                  {{ element.username }}
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="status">
                <mat-header-cell *matHeaderCellDef mat-sort-header> Status </mat-header-cell>
                <mat-cell *matCellDef="let element">
                  <div class="small-font">{{ element?.status }}</div>
                  @if (element.status === EventStatus.Failed) {
                  <div class="small-font">
                    &nbsp;{{ element.internalStatus }}
                  </div>
                  }
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="lastLaunchStatus">
                <mat-header-cell *matHeaderCellDef mat-sort-header>
                  Last Launch
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                  @if (element.lastLaunchStatus) {
                  <div class="small-font">
                    {{ element.lastLaunchStatus }}
                    {{ element.lastLaunchInternalStatus }}
                  </div>
                  }
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="lastEndStatus">
                <mat-header-cell *matHeaderCellDef mat-sort-header>
                  Last End Status</mat-header-cell>
                <mat-cell *matCellDef="let element">
                  @if (element.lastEndStatus) {
                  <div class="small-font">
                    {{ element.lastEndStatus }}
                    {{ element.lastEndInternalStatus }}
                  </div>
                  }
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="dateCreated">
                <mat-header-cell *matHeaderCellDef mat-sort-header>
                  Start Date Status</mat-header-cell>
                <mat-cell *matCellDef="let element">
                  <div class="small-font">
                    {{ element.dateCreated | date: 'long' }}
                  </div>
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="endDate">
                <mat-header-cell *matHeaderCellDef mat-sort-header> End Date </mat-header-cell>
                <mat-cell *matCellDef="let element">
                  <div class="small-font">
                    {{ element.endDate | date: 'long' }}
                  </div>
                </mat-cell>
              </ng-container>
              <ng-container matColumnDef="statusDate">
                <mat-header-cell *matHeaderCellDef mat-sort-header>
                  Last Status Date
                </mat-header-cell>
                <mat-cell *matCellDef="let element">
                  @if (element.status === 'Failed') {
                  <div class="small-font">
                    {{ element.statusDate | date: 'long' }}
                  </div>
                  }
                </mat-cell>
              </ng-container>
              <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></mat-header-row>
              <mat-row *matRowDef="let row; columns: displayedColumns"></mat-row>
            </mat-table>
            @if (impsDataSource.filteredData.length === 0) {
            <div>
              No results found
            </div>
            }
          </div>
        </mat-expansion-panel>
      </mat-accordion>
      }
    </div>
    }
  </div>
</div>
</div>
